import { useState } from "react";
import { Check, X } from "lucide-react";
import {
  type Identifier,
  Translate,
  useGetIdentity,
  useGetList,
  useGetOne,
  useRecordContext,
} from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { InlineAvatar } from "../misc/InlineAvatar";
import type {
  Contact,
  Sale,
  ZoomMeetingParticipant,
  ZoomMeetingSummary,
} from "../types";
import {
  applyBinding,
  deriveAssigneeRows,
  type AssigneeBinding,
  type AssigneeRow,
} from "./assigneeRows";
import { matchAttendee, type ResolverParticipant } from "./resolveAssignee";

// Centralized assignee control for a meeting's tasks: one row per distinct Next-Steps
// name, showing who it resolved to (teammate / external contact / needs-assignment) with
// a picker that re-assigns EVERY task sharing that name at once. Reads/writes the in-form
// `tasks` array (tasks aren't persisted until "Add note and create tasks").

const AMBER =
  "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300";

const initials = (p?: { first_name?: string; last_name?: string }) =>
  p ? `${p.first_name?.[0] ?? ""}${p.last_name?.[0] ?? ""}`.toUpperCase() : "?";

const fullName = (p?: { first_name?: string; last_name?: string }) =>
  p ? `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() : "";

export const AssigneeResolutionPanel = () => {
  const { setValue } = useFormContext();
  const { identity } = useGetIdentity();
  const record = useRecordContext<ZoomMeetingSummary>();
  const tasks = (useWatch({ name: "tasks" }) ??
    []) as ZoomMeetingSummary["tasks"];
  const rows = deriveAssigneeRows(tasks);
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });
  // The meeting roster bridges a terse task name ("Josh") to the attendee's fuller phone
  // name + email ("Josh Kerr") — a far better key for suggesting a contact. (ReParseButton
  // fetches the same list; React Query dedupes.)
  const { data: roster = [] } = useGetList<ZoomMeetingParticipant>(
    "zoom_meeting_participants",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
      filter: { meeting_ref: record?.id },
    },
    { enabled: record?.id != null },
  );

  if (rows.length === 0) return null;

  const unresolved = rows.filter((r) => r.binding.kind === "unresolved").length;

  const onPick = (name: string, binding: AssigneeBinding) => {
    setValue("tasks", applyBinding(tasks, name, binding, identity?.id), {
      shouldDirty: true,
    });
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/50">
        <span className="text-sm font-medium text-muted-foreground flex-1">
          Assignees
        </span>
        {unresolved > 0 && (
          <Badge variant="outline" className={cn("text-[10px]", AMBER)}>
            {unresolved} need assignment
          </Badge>
        )}
      </div>
      {/* One compact line per name, full height (no internal scroll) — the
          Tasks panel's own scrollbar is the only one in this region. */}
      <div className="flex flex-col gap-1 p-2">
        {rows.map((row) => (
          <AssigneeRowView
            key={row.name}
            row={row}
            sales={sales}
            roster={roster}
            onPick={onPick}
          />
        ))}
      </div>
    </div>
  );
};

// One compact chip per Next-Steps name: parsed name (×count) → who it resolved
// to → picker. Unresolved chips also carry the suggestion buttons.
const AssigneeRowView = ({
  row,
  sales,
  roster,
  onPick,
}: {
  row: AssigneeRow;
  sales: Sale[];
  roster: ResolverParticipant[];
  onPick: (name: string, binding: AssigneeBinding) => void;
}) => (
  <div className="flex w-full items-center gap-1.5 rounded-md border px-2 py-1 min-w-0">
    <span
      className="text-xs font-medium shrink-0 max-w-28 truncate"
      title={row.count > 1 ? `${row.name} — ${row.count} tasks` : row.name}
    >
      {row.name}
      {row.count > 1 && (
        <span className="text-muted-foreground font-normal"> ×{row.count}</span>
      )}
    </span>
    <BindingDisplay binding={row.binding} sales={sales} />
    {/* Clearing a binding sits beside the name it clears, not inside the picker: the
        picker is a list of people to choose, and "undo" is not one of them. It is also
        the only control here that puts a row back into the state that blocks
        processing, so it should be visibly separate from the ones that resolve it. */}
    {row.binding.kind !== "unresolved" && (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={`Clear the assignee for ${row.name}`}
        title="Clear this assignee"
        className="h-5 w-5 p-0 shrink-0 text-muted-foreground hover:text-foreground"
        onClick={() => onPick(row.name, { kind: "unresolved" })}
      >
        <X className="h-3 w-3" />
      </Button>
    )}
    <span className="ml-auto flex items-center gap-1 min-w-0">
      {row.binding.kind === "unresolved" && (
        <Suggestions
          name={row.name}
          roster={roster}
          sales={sales}
          onPick={(b) => onPick(row.name, b)}
        />
      )}
      <AssigneePicker
        binding={row.binding}
        sales={sales}
        onPick={(b) => onPick(row.name, b)}
      />
    </span>
  </div>
);

const BindingDisplay = ({
  binding,
  sales,
}: {
  binding: AssigneeBinding;
  sales: Sale[];
}) => {
  if (binding.kind === "sales") {
    const s = sales.find((x) => x.id === binding.sales_id);
    return (
      <span className="flex items-center gap-1.5 text-xs min-w-0">
        <InlineAvatar src={s?.avatar?.src} fallback={initials(s)} />
        <span className="truncate max-w-32">
          {s ? fullName(s) : `Sales #${binding.sales_id}`}
        </span>
        <Badge variant="secondary" className="text-[10px]">
          Sales
        </Badge>
      </span>
    );
  }
  if (binding.kind === "contact") {
    return <ContactBadge contactId={binding.contact_id} />;
  }
  if (binding.kind === "external") {
    // Attended the call, isn't a teammate, and we hold no contact for them. Same
    // treatment as a known customer — hidden, never created — with no record to link
    // to. The picker beside this badge rebinds it if the guess was wrong.
    return (
      <span className="flex items-center gap-1.5 text-xs min-w-0">
        <Badge variant="secondary" className="text-[10px]">
          Outside guest
        </Badge>
        <Badge variant="secondary" className="text-[10px]">
          <Translate i18nKey="resources.zoom_meeting_summaries.external_hidden" />
        </Badge>
      </span>
    );
  }
  return (
    <Badge variant="outline" className={cn("text-[10px]", AMBER)}>
      Needs assignment
    </Badge>
  );
};

const ContactBadge = ({ contactId }: { contactId: number | string }) => {
  const { data: c } = useGetOne<Contact>(
    "contacts",
    { id: contactId },
    { enabled: contactId != null },
  );
  return (
    <span className="flex items-center gap-1.5 text-xs min-w-0">
      <InlineAvatar src={c?.avatar?.src} fallback={initials(c)} />
      <span className="truncate max-w-32">
        {c ? fullName(c) : `Contact #${contactId}`}
      </span>
      {/* Customer-owned: these tasks are hidden from the list and never
          created — rebind the name to a teammate to bring them back. */}
      <Badge variant="secondary" className="text-[10px]">
        <Translate i18nKey="resources.zoom_meeting_summaries.external_hidden" />
      </Badge>
    </span>
  );
};

// Close-match candidates for an unresolved name, bridged through the meeting attendee:
// the terse task name ("Josh") maps to the attendee's fuller phone name + email
// ("Josh Kerr"), so we search Contacts by the attendee's email (exact-ish) and the
// last-name token of the attendee's full name — landing on "Joshua Kerr" precisely rather
// than every "Josh". Matching TEAMMATES are surfaced first (team-first). Falls back to the
// task name when no attendee matches.
const Suggestions = ({
  name,
  roster,
  sales,
  onPick,
}: {
  name: string;
  roster: ResolverParticipant[];
  sales: Sale[];
  onPick: (binding: AssigneeBinding) => void;
}) => {
  const attendee = matchAttendee(name, roster);
  const email = attendee?.user_email ?? "";
  const nameKey = (attendee?.name ?? name).trim();
  const tokens = nameKey.split(/\s+/);
  const lastToken = tokens[tokens.length - 1];

  // Teammate candidates by name (the picker has the full list; these are the quick hits).
  const key = nameKey.toLowerCase();
  const teammateHits = sales
    .filter((s) => {
      const f = (s.first_name ?? "").trim().toLowerCase();
      const l = (s.last_name ?? "").trim().toLowerCase();
      return (
        fullName(s).toLowerCase().includes(key) ||
        tokens.some((t) => t === f || t === l)
      );
    })
    .slice(0, 3);

  const { data: byName = [] } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 5 },
      sort: { field: "last_name", order: "ASC" },
      filter: { q: lastToken },
    },
    { enabled: !!lastToken },
  );
  const { data: byEmail = [] } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 3 },
      sort: { field: "last_name", order: "ASC" },
      filter: { q: email },
    },
    { enabled: !!email },
  );

  // Teammates come first; contacts fill the rest so the whole list stays at 3 chips.
  // Email hits first (exact-ish), then same-last-name; deduped.
  const contactSlots = Math.max(0, 3 - teammateHits.length);
  const seen = new Set<Identifier>();
  const suggestions: Contact[] = [];
  for (const c of [...byEmail, ...byName]) {
    if (suggestions.length >= contactSlots) break;
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    suggestions.push(c);
  }
  if (teammateHits.length === 0 && suggestions.length === 0) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap justify-end">
      {teammateHits.map((s) => (
        <button
          key={`s${s.id}`}
          type="button"
          title={`Assign ${fullName(s)} (sales)`}
          onClick={() => onPick({ kind: "sales", sales_id: s.id })}
          className="text-xs px-1.5 py-0.5 rounded border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 max-w-40 truncate"
        >
          {fullName(s)}
        </button>
      ))}
      {suggestions.map((c) => (
        <button
          key={c.id}
          type="button"
          title={`Assign ${fullName(c)}`}
          onClick={() => onPick({ kind: "contact", contact_id: c.id })}
          className="text-xs px-1.5 py-0.5 rounded border bg-background hover:bg-muted max-w-32 truncate"
        >
          {fullName(c)}
        </button>
      ))}
    </div>
  );
};

const AssigneePicker = ({
  binding,
  sales,
  onPick,
}: {
  binding: AssigneeBinding;
  sales: Sale[];
  onPick: (binding: AssigneeBinding) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const { data: contacts } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 20 },
      sort: { field: "last_name", order: "ASC" },
      filter: { q: search },
    },
    { enabled: open && q.length > 0 },
  );
  const teammates = q
    ? sales.filter((s) => fullName(s).toLowerCase().includes(q))
    : sales;
  const bound = binding.kind !== "unresolved";

  const pick = (b: AssigneeBinding) => {
    onPick(b);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-6 px-2 text-xs shrink-0"
        >
          {/* Not "Assign": this button assigns nothing, it opens a search over
              every teammate and contact. The chips beside it are what assign on
              click. The ellipsis is the usual signal that something opens, and it
              still reads correctly when there are no chips at all, which happens
              whenever nothing matched. */}
          {bound ? "Change" : "Search…"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search sales or contacts…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No matches.</CommandEmpty>
            {/* First, because it is the answer for most rows that reach this picker.
                The resolver marks roster attendees external on its own, but only when
                the name matches exactly one of them. A name matching nobody, or two
                people at once, has no automatic answer, and used to leave the reviewer
                with no honest option: invent a contact, hand a customer's commitment to
                a teammate, or delete it. This is that option. */}
            <CommandGroup heading="Not ours to do">
              <CommandItem
                value="__external"
                onSelect={() => pick({ kind: "external" })}
                className="cursor-pointer"
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    binding.kind === "external" ? "" : "invisible",
                  )}
                />
                <span className="truncate">
                  Outside guest
                  <span className="ml-1 text-xs text-muted-foreground">
                    · no task created
                  </span>
                </span>
              </CommandItem>
            </CommandGroup>
            {teammates.length > 0 && (
              <CommandGroup heading="Teammates">
                {teammates.map((s) => (
                  <CommandItem
                    key={`s${s.id}`}
                    value={`s${s.id}`}
                    onSelect={() => pick({ kind: "sales", sales_id: s.id })}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        binding.kind === "sales" && binding.sales_id === s.id
                          ? ""
                          : "invisible",
                      )}
                    />
                    <InlineAvatar
                      src={s.avatar?.src}
                      fallback={initials(s)}
                      className="mr-2"
                    />
                    <span className="truncate">{fullName(s)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {/* Contacts only load once something is typed. Teammates are a handful and
                list in full; contacts number in the thousands, so an unfiltered page of
                20 sorted by surname is noise. Say so rather than rendering nothing: an
                empty Contacts section reads as a broken picker.

                The heading repeats "not ours to do" rather than reading a bare
                "Contacts". Since LCATOMICZO-31 the draft row carries its own Contact
                picker, which means "this task is about them" — the opposite of
                assigning the work to them. Same people in both lists, so the headings
                have to carry the difference. It cannot simply join the "Not ours to
                do" group above: that group sits first because Outside guest is the
                common answer, while contacts need typing, so they stay last. */}
            {q.length === 0 && (
              <CommandGroup heading="Contacts — not ours to do">
                <CommandItem
                  value="__contacts_hint"
                  disabled
                  className="text-xs text-muted-foreground"
                >
                  Type a name to search contacts
                </CommandItem>
              </CommandGroup>
            )}
            {q.length > 0 && (contacts?.length ?? 0) > 0 && (
              <CommandGroup heading="Contacts — not ours to do">
                {contacts!.map((c) => (
                  <CommandItem
                    key={`c${c.id}`}
                    value={`c${c.id}`}
                    onSelect={() => pick({ kind: "contact", contact_id: c.id })}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        binding.kind === "contact" &&
                          binding.contact_id === c.id
                          ? ""
                          : "invisible",
                      )}
                    />
                    <InlineAvatar
                      src={c.avatar?.src}
                      fallback={initials(c)}
                      className="mr-2"
                    />
                    <span className="truncate">
                      {fullName(c)}
                      {c.company_name ? (
                        <span className="ml-1 text-xs text-muted-foreground">
                          · {c.company_name}
                        </span>
                      ) : null}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {/* No "Unassign" here. Clearing a binding is the × beside the name, so this
                list only ever offers something to assign TO. */}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
